/**
 * The custom player motor. Owns the per-tick movement pipeline:
 *
 *  1. intent       InputFrame → MovementIntent (wish direction, buffered jump)
 *                  then posture and slide start (Techniques.begin)
 *  2. surroundings proximity query for rules (only if an enabled rule needs it)
 *  3. locomotion   slide, landing grace, ground, wall-run or air model; gravity
 *  4. jump         ground/coyote jump, else wall-jump; jump-stage rules
 *  5. rules        field accelerations, then ordered velocity transforms
 *  6. collision    swept collide-and-slide, impact-stage rules per hit
 *  7. ground       probe + snap, grounded bookkeeping; walls and wall-runs (Techniques.sense)
 *  8. consequences lethal contact, contact records, diagnostics
 *
 * Rendering, input devices and level visuals are invisible from here.
 */
import { Vec3 } from '../core/Vec3';
import type { EngineConfig, MovementConfig } from '../core/Config';
import type { InputFrame } from '../input/InputFrame';
import { ProximityContact, type CapsuleShape, type CollisionWorld, type SweepHit } from '../simulation/CollisionQueries';
import { ContactResolver, MotionResult } from './ContactResolver';
import { Locomotion } from './Locomotion';
import { Techniques } from './Techniques';
import { MovementRuleSet, type EnergyPolicy, type ImpactInfo, type MovementIntent, type MovementRule, type RuleContext } from './MovementRules';
import type { PlayerState, RuleState } from './PlayerState';

export interface MotorDiagnostics {
  readonly motion: MotionResult;
  readonly nearbyCount: number;
  readonly wishDir: Readonly<Vec3>;
  ruleViolations: number;
  numericalFaults: number;
  stepMs: number;
  lastRuleViolation: string | null;
}

class Intent implements MovementIntent {
  readonly wishDir = new Vec3();
  wishStrength = 0;
  jumpRequested = false;
  jumpHeld = false;
}

const now = (): number => globalThis.performance.now();

export class PlayerMotor {
  readonly shape: CapsuleShape;
  readonly resolver: ContactResolver;
  readonly rules: MovementRuleSet;
  private readonly cfg: Readonly<MovementConfig>;
  private readonly locomotion: Locomotion;
  readonly techniques: Techniques;
  private readonly intent = new Intent();
  private readonly motion = new MotionResult();
  private readonly nearby: ProximityContact[] = [];
  private nearbyCount = 0;
  private readonly accel = new Vec3();
  private readonly ruleAccel = new Vec3();
  private readonly impulse = new Vec3();
  private readonly ruleImpulse = new Vec3();
  private readonly jumpDir = new Vec3();
  private readonly forward = new Vec3();
  private readonly right = new Vec3();
  private readonly impact: { normal: Vec3; incoming: Vec3; impactSpeed: number; material: ImpactInfo['material']; colliderId: number } = {
    normal: new Vec3(),
    incoming: new Vec3(),
    impactSpeed: 0,
    material: null,
    colliderId: -1,
  };
  private readonly ctx: { -readonly [K in keyof RuleContext]: RuleContext[K] };
  readonly diagnostics: MotorDiagnostics;
  private readonly impactHandler: (n: Readonly<Vec3>, incoming: Readonly<Vec3>, speed: number, hit: SweepHit, v: Vec3) => void;

  constructor(world: CollisionWorld, config: Readonly<EngineConfig>, rules: MovementRuleSet = new MovementRuleSet()) {
    this.cfg = config.movement;
    const r = config.player.radius;
    this.shape = { radius: r, halfSegment: Math.max(0, config.player.height / 2 - r) };
    this.resolver = new ContactResolver(world, this.shape, this.cfg);
    this.locomotion = new Locomotion(this.cfg);
    this.techniques = new Techniques(config, this.shape, this.resolver);
    this.rules = rules;
    this.ctx = {
      state: null as unknown as PlayerState,
      intent: this.intent,
      movement: this.cfg,
      dt: 0,
      nearby: this.nearby,
      nearbyCount: 0,
      ruleState: null,
    };
    this.diagnostics = {
      motion: this.motion,
      nearbyCount: 0,
      wishDir: this.intent.wishDir,
      ruleViolations: 0,
      numericalFaults: 0,
      stepMs: 0,
      lastRuleViolation: null,
    };
    this.impactHandler = (n, incoming, speed, hit, v) => this.runImpactRules(n, incoming, speed, hit, v);
  }

  /** Ensures every registered rule's persistent state exists on `state`. */
  attach(state: PlayerState): void {
    for (const rule of this.rules.list()) {
      const key = rule.stateKey ?? rule.id;
      if (rule.createState && !state.ruleState.has(key)) state.ruleState.set(key, rule.createState());
    }
  }

  /** Advances `state` by one fixed tick of length `dt`. Returns the motion result for consequences. */
  step(state: PlayerState, input: Readonly<InputFrame>, dt: number): MotionResult {
    const t0 = now();
    const cfg = this.cfg;
    const motion = this.motion;
    motion.reset();
    state.previousPosition.copy(state.position);
    state.teleported = false;
    if (!state.alive) {
      state.tick++;
      return motion;
    }
    this.attach(state);
    this.buildIntent(state, input);
    this.techniques.begin(state, input.crouchHeld);
    this.ctx.state = state;
    this.ctx.dt = dt;
    const v = state.velocity;
    const up = state.up;
    const gravity = cfg.gravity * state.gravityScale;
    const tech = this.techniques;

    // 2. surroundings
    const range = Math.max(this.rules.maxProximityRange(), 0);
    this.nearbyCount = range > 0 ? this.resolver.nearby(state.position, up, range, this.nearby) : 0;
    this.ctx.nearbyCount = this.nearbyCount;

    // 3. locomotion + gravity
    const groundedAtStart = state.grounded;
    if (groundedAtStart && state.sliding) {
      tech.slide(v, this.intent, state.groundNormal, up, gravity, dt);
    } else if (groundedAtStart && tech.inLandingGrace(state)) {
      // Landing grace: no friction or steering yet, so a jump now keeps all speed.
      this.locomotion.coast(v, state.groundNormal, up, gravity, dt);
    } else if (groundedAtStart) {
      this.locomotion.ground(v, this.intent, state.groundNormal, up, gravity, state.groundMaterial, dt, state.crouched);
    } else if (state.wallRunTicks > 0) {
      const scale = tech.wallRun(state, v, dt);
      v.addScaled(up, -gravity * scale * dt);
    } else {
      this.locomotion.air(v, this.intent, up, dt);
      v.addScaled(up, -gravity * dt);
    }

    // 4. jump
    const jumped = this.resolveJump(state);

    // 5. rules: fields (summed), then ordered transforms
    this.accel.set(0, 0, 0);
    for (const rule of this.rules.stage('field')) {
      if (!this.prepare(rule, state)) continue;
      this.ruleAccel.set(0, 0, 0);
      rule.accumulate(this.ctx, rule.params, this.ruleAccel);
      if (this.ruleAccel.isFinite()) this.accel.add(this.ruleAccel);
      else this.violation(rule.id, 'non-finite acceleration');
    }
    v.addScaled(this.accel, dt);
    for (const rule of this.rules.stage('transform')) {
      if (!this.prepare(rule, state)) continue;
      const before = v.length();
      rule.transform(this.ctx, rule.params, v);
      this.enforceEnergy(rule.id, rule.energy, before, v);
    }

    // 6. collision
    this.sanitize(state);
    this.resolver.move(state.position, v, up, dt, this.rules.stage('impact').length > 0 ? this.impactHandler : null, motion);

    // 7. ground
    if (!motion.lethal) {
      const allowSnap = groundedAtStart && !jumped && state.ticksSinceJump > cfg.jumpSnapSuppressTicks;
      const g = this.resolver.probeGround(state.position, v, up, allowSnap && v.dot(up) <= cfg.snapMaxSeparationSpeed, motion, groundedAtStart ? state.groundNormal : null);
      state.grounded = g.grounded && !jumped;
      if (state.grounded && !groundedAtStart) state.groundedTicks = 0;
      if (state.grounded) {
        state.groundNormal.copy(g.normal);
        state.groundColliderId = g.colliderId;
        state.groundMaterial = g.material;
        state.ticksSinceGrounded = 0;
      } else {
        state.ticksSinceGrounded++;
      }
    }
    state.ticksSinceJump++;
    if (!motion.lethal) tech.sense(state, this.intent, motion, jumped);

    // 8. consequences
    (this.diagnostics as { nearbyCount: number }).nearbyCount = this.nearbyCount;
    this.recordContacts(state);
    this.sanitize(state);
    state.tick++;
    this.diagnostics.stepMs = now() - t0;
    return motion;
  }

  private buildIntent(state: PlayerState, input: Readonly<InputFrame>): void {
    state.yaw = input.yaw;
    state.pitch = input.pitch;
    const it = this.intent;
    // Yaw 0 faces -Z. Basis is projected onto the plane perpendicular to `up`.
    this.forward.set(-Math.sin(input.yaw), 0, -Math.cos(input.yaw)).projectOnPlane(state.up).normalize();
    this.right.crossVectors(this.forward, state.up).normalize();
    const mx = Math.max(-1, Math.min(1, input.moveX));
    const mz = Math.max(-1, Math.min(1, input.moveZ));
    it.wishDir.set(0, 0, 0).addScaled(this.forward, mz).addScaled(this.right, mx);
    const len = it.wishDir.length();
    it.wishStrength = Math.min(1, len);
    if (len > 1e-9) it.wishDir.scale(1 / len);
    if (input.jumpPressed) state.jumpBuffer = this.cfg.jumpBufferTicks;
    else if (state.jumpBuffer > 0) state.jumpBuffer--;
    it.jumpRequested = state.jumpBuffer > 0;
    it.jumpHeld = input.jumpHeld;
  }

  private resolveJump(state: PlayerState): boolean {
    if (!this.intent.jumpRequested) return false;
    const cfg = this.cfg;
    const v = state.velocity;
    const groundJump = (state.grounded || state.ticksSinceGrounded <= cfg.coyoteTicks) && state.ticksSinceJump > cfg.coyoteTicks;
    this.impulse.set(0, 0, 0);
    let consumed = false;
    if (groundJump) {
      const b = cfg.jumpNormalBlend;
      this.jumpDir.copy(state.up).scale(1 - b).addScaled(state.groundNormal, b).normalize();
      const into = v.dot(this.jumpDir);
      if (into < 0) v.addScaled(this.jumpDir, -into);
      this.impulse.addScaled(this.jumpDir, cfg.jumpSpeed);
      consumed = true;
      state.sliding = false;
    }
    let ruleJump = false;
    for (const rule of this.rules.stage('jump')) {
      if (!this.prepare(rule, state)) continue;
      this.ruleImpulse.set(0, 0, 0);
      const used = rule.contribute(this.ctx, rule.params, groundJump, this.ruleImpulse);
      if (!this.ruleImpulse.isFinite()) {
        this.violation(rule.id, 'non-finite jump impulse');
        continue;
      }
      this.impulse.add(this.ruleImpulse);
      ruleJump ||= used;
    }
    consumed ||= ruleJump;
    // A rule's jump (a compression release) replaces the wall-jump rather than stacking on it.
    if (!consumed && this.techniques.wallJump(state, v)) consumed = true;
    if (!consumed) return false;
    v.add(this.impulse);
    state.jumpBuffer = 0;
    state.ticksSinceJump = 0;
    state.grounded = false;
    state.ticksSinceGrounded = Math.max(state.ticksSinceGrounded, cfg.coyoteTicks + 1);
    return true;
  }

  private runImpactRules(n: Readonly<Vec3>, incoming: Readonly<Vec3>, speed: number, hit: SweepHit, v: Vec3): void {
    const info = this.impact;
    info.normal.copy(n);
    info.incoming.copy(incoming);
    info.impactSpeed = speed;
    info.material = hit.material;
    info.colliderId = hit.colliderId;
    const state = this.ctx.state as PlayerState;
    for (const rule of this.rules.stage('impact')) {
      if (!this.prepare(rule, state)) continue;
      // Impact rules are bounded by the *incoming* energy: they may redistribute
      // what the slide response removed, never create more.
      const limit = incoming.length();
      rule.onImpact(this.ctx, rule.params, info, v);
      if (!v.isFinite()) {
        this.violation(rule.id, 'non-finite impact velocity');
        v.copy(incoming).projectOnPlane(n);
      } else if (v.length() > limit * (1 + 1e-9) + 1e-9) {
        this.violation(rule.id, 'impact response exceeded incoming speed');
        v.scale(limit / v.length());
      }
      // The response must not drive the player back into the surface.
      const vn = v.dot(n);
      if (vn < 0) v.addScaled(n, -vn);
    }
  }

  private prepare(rule: MovementRule, state: PlayerState): boolean {
    this.ctx.ruleState = (state.ruleState.get(rule.stateKey ?? rule.id) as RuleState | undefined) ?? null;
    return rule.applies(this.ctx, rule.params);
  }

  private enforceEnergy(id: string, policy: EnergyPolicy, before: number, v: Vec3): void {
    if (!v.isFinite()) {
      this.violation(id, 'non-finite velocity');
      v.set(0, 0, 0);
      return;
    }
    const after = v.length();
    if (policy === 'conserve' && Math.abs(after - before) > 1e-6 * Math.max(1, before)) {
      this.violation(id, `conserve policy changed speed ${before.toFixed(4)} → ${after.toFixed(4)}`);
      if (after > 0) v.scale(before / after);
    } else if (policy === 'dissipate' && after > before * (1 + 1e-9) + 1e-9) {
      this.violation(id, 'dissipate policy increased speed');
      v.scale(before / after);
    }
  }

  private violation(id: string, what: string): void {
    this.diagnostics.ruleViolations++;
    this.diagnostics.lastRuleViolation = `${id}: ${what}`;
  }

  /** Guards against numerical faults. This is not a gameplay speed cap. */
  private sanitize(state: PlayerState): void {
    const v = state.velocity;
    if (!v.isFinite() || !state.position.isFinite()) {
      this.diagnostics.numericalFaults++;
      v.set(0, 0, 0);
      if (!state.position.isFinite()) {
        state.position.copy(state.previousPosition);
        state.alive = false;
        state.deathCause = 'numerical-fault';
      }
      return;
    }
    const s = v.length();
    if (s > this.cfg.numericalSpeedLimit) {
      this.diagnostics.numericalFaults++;
      v.scale(this.cfg.numericalSpeedLimit / s);
    }
  }

  private recordContacts(state: PlayerState): void {
    const n = this.resolver.nearby(state.position, state.up, this.cfg.lethalContactDistance, this.nearby);
    let count = 0;
    for (let i = 0; i < n; i++) {
      const c = this.nearby[i]!;
      if (!c.material) continue;
      if (c.material.kind === 'lethal' && !this.motion.lethal) this.motion.setLethal(c.colliderId, 1, c.material);
      let rec = state.contacts[count];
      if (!rec) {
        rec = { colliderId: -1, materialId: '', kind: 'safe', normal: new Vec3(), distance: 0 };
        state.contacts[count] = rec;
      }
      rec.colliderId = c.colliderId;
      rec.materialId = c.material.id;
      rec.kind = c.material.kind;
      rec.normal.copy(c.normal);
      rec.distance = c.distance;
      count++;
    }
    state.contactCount = count;
    // The proximity buffer was reused; rules must not read it after this point.
    this.nearbyCount = 0;
  }
}
